import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { compact, isEmpty, toUpper } from 'lodash'
import cn from 'classnames'

import { shapePaginator } from 'resources/typeDefs'
import Loader from 'components/Loader'
import List from 'components/List'
import ListItem from 'components/List/ListItem'
import Pagination from 'components/Pagination'
import CheckboxWithLabel from 'components/CheckboxWithLabel'
import BulkAction from 'components/BulkAction'
import DialogModal from 'components/DialogModal'

import { setCurrentPaginationPage } from 'store/AppState/actions'
import { deleteConfiguration } from 'store/Configurations/actions'
import { fetchDevices, bulkUpdateDevice } from 'store/Devices/actions'
import { getCurrentPaginationPage, getDevicesFilter } from 'store/AppState/selector'
import { getCurrentVenueId, getCurrentVenue } from 'store/Venues/selector'

import { MdContentCopy, MdCheck } from 'react-icons/md'

const HardwareIndex = ({ paginator, isLoadingDevices }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [selectedDevicesIds, setSelectedDevices] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSelectAllCheckboxChecked, setIsSelectAllCheckboxChecked] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [copyIndex, setCopyIndex] = useState(null)

  const currentVenueId = useSelector(getCurrentVenueId)
  const currentVenue = useSelector(getCurrentVenue)
  const currentPaginationPage = useSelector((state) => getCurrentPaginationPage(state, 'hardware'))
  const devicesFilter = useSelector((state) => getDevicesFilter(state, 'status'))

  const handleUnlockDevice = () => {
    const selectedDevices = paginator.result
      .filter((device) => selectedDevicesIds.includes(device.deviceId))
      .map((item) => ({
        deviceId: item.deviceId,
        lock: false,
        venueUuid: currentVenueId,
      }))

    setIsLoading(true)
    dispatch(bulkUpdateDevice({ devices: selectedDevices }))
      .then(() => dispatch(fetchDevices(currentVenueId)), setSelectedDevices([]))
      .then(() => setIsLoading(false))
  }

  const handleLockDevice = () => {
    const selectedDevices = paginator.result
      .filter((device) => selectedDevicesIds.includes(device.deviceId))
      .map((item) => ({
        deviceId: item.deviceId,
        lock: true,
        venueUuid: null,
        organizationName: currentVenue.label,
      }))

    const linkedConfigurations = selectedDevices.reduce((result, current) => {
      const configuration = paginator.result.find((device) => {
        return device.deviceId === current.deviceId
      })

      return [...result, configuration.configuration.uuid]
    }, [])

    const validLinkedConfigurations = compact(linkedConfigurations)

    const deleteAllRequests = () => {
      // It is temporary, because it sends as many request
      // as we have devices selected
      // will be changed when API allow us to bulk delete configurations

      // if there is no linked configurations
      // we don't need to send any request
      if (isEmpty(validLinkedConfigurations)) {
        return Promise.resolve()
      }

      let requests = []
      for (let k = 0; k < validLinkedConfigurations.length; k++) {
        let ids1 = validLinkedConfigurations[k]
        requests.push(dispatch(deleteConfiguration(ids1)))
      }
      return Promise.all(requests)
    }

    setIsLoading(true)
    dispatch(bulkUpdateDevice({ devices: selectedDevices }))
      .then(() => deleteAllRequests())
      .then(() => dispatch(fetchDevices(currentVenueId)), setSelectedDevices([]))
      .then(() => setIsLoading(false))
  }

  useEffect(() => {
    // Deselect all selected checkboxes after switching Pending/Registered
    setSelectedDevices([])

    return () => {
      setSelectedDevices([])
    }
  }, [devicesFilter])

  useEffect(
    () => {
      if (paginator.result.length === selectedDevicesIds.length) {
        setIsSelectAllCheckboxChecked(true)
      } else {
        setIsSelectAllCheckboxChecked(false)
      }
    },
    // eslint-disbale-next-line
    [paginator.result.length, selectedDevicesIds.length],
  )

  const handlePersistPaginationPage = (page) => {
    setCurrentPaginationPage({ screen: 'hardware', page })
  }

  const handleSelectAllCheckboxes = (checked) => {
    const allCheckboxIds = paginator.result.map((device) => device.deviceId)

    if (!checked) {
      setIsSelectAllCheckboxChecked(true)
      setSelectedDevices([])
    } else {
      setIsSelectAllCheckboxChecked(false)
      setSelectedDevices(allCheckboxIds)
    }
  }

  const headers = [
    <CheckboxWithLabel
      checked={isSelectAllCheckboxChecked}
      key="test"
      onChange={handleSelectAllCheckboxes}
    />,
    'Serial NO.',
    'Linked Config',
    'Type',
    'Registered Status',
  ]

  const dialogModalContent = (
    <div className="padding-base">
      These devices will no longer be available to the venue for ordering. Are you sure you want to
      deregister these devices?
    </div>
  )

  const handleDeviceItemChange = (checked, event) => {
    const { value } = event.target
    setSelectedDevices((prevState) => {
      return checked ? [...prevState, value] : prevState.filter((item) => item !== value)
    })
  }

  const copyId = (e, deviceId, index) => {
    e.stopPropagation()
    setCopyIndex(index)
    navigator.clipboard.writeText(deviceId)
    setTimeout(() => setCopyIndex(null), 1500)
  }

  if (isLoadingDevices || isLoading) {
    return <Loader />
  }

  return (
    <div className="devices-list">
      <List headerClasses={['-small', 0]} headerItems={headers}>
        {paginator?.result?.map(
          ({ deviceId, uuid, configuration: { name }, appType, lockedAt }, index) => (
            <div
              className={cn('item', { '-selected': selectedDevicesIds.includes(deviceId) })}
              key={uuid}
            >
              <div className="row">
                <CheckboxWithLabel
                  checked={selectedDevicesIds.includes(deviceId)}
                  onChange={handleDeviceItemChange}
                  value={deviceId}
                />
                <div
                  className="item-info-wrapper"
                  onClick={() => history.push(`/devices/${deviceId}/edit`)}
                >
                  <span className="copy-button" onClick={(e) => copyId(e, deviceId, index)}>
                    {copyIndex === index ? <MdCheck /> : <MdContentCopy />}
                  </span>
                  <ListItem content={deviceId} />
                </div>
                <div
                  className="item-info-wrapper"
                  onClick={() => history.push(`/devices/${deviceId}/edit`)}
                >
                  <ListItem content={name} />
                </div>
                <div
                  className="item-info-wrapper"
                  onClick={() => history.push(`/devices/${deviceId}/edit`)}
                >
                  <ListItem content={toUpper(appType)} />
                </div>
                <div
                  className="item-info-wrapper"
                  onClick={() => history.push(`/devices/${deviceId}/edit`)}
                >
                  <ListItem content={isEmpty(lockedAt) ? 'Registered' : 'Pending'} />
                </div>
              </div>
            </div>
          ),
        )}
      </List>
      <section className="footer-pagination">
        {devicesFilter === 'pending' ? (
          <BulkAction
            buttonClassname="-primary -sm"
            buttonDisabled={isEmpty(selectedDevicesIds)}
            buttonText="Register"
            handleClick={handleUnlockDevice}
            number={selectedDevicesIds.length}
          />
        ) : (
          <BulkAction
            buttonClassname="-danger -sm"
            buttonDisabled={isEmpty(selectedDevicesIds)}
            buttonText="Deregister"
            handleClick={() => setIsModalOpen(true)}
            number={selectedDevicesIds.length}
          />
        )}
        <Pagination
          currentPersistedPaginationPage={currentPaginationPage}
          handlePersistPaginationPage={handlePersistPaginationPage}
          paginator={paginator}
        />
      </section>
      <DialogModal
        closeModalCallback={() => setIsModalOpen(false)}
        confirmationCallback={handleLockDevice}
        modalBody={dialogModalContent}
        modalButtonText="OK"
        modalIsOpen={isModalOpen}
        modalTitle="Confirm Deregister"
      />
    </div>
  )
}

HardwareIndex.defaultProps = {
  emptyStateTitle: 'No items found!',
  emptySearchResultMessage: 'There are no items matching given search query.',
  emptySearchResultTitle: "We couldn't find what you were looking for",
  isFilteringItems: false,
}

HardwareIndex.propTypes = {
  paginator: shapePaginator,
  isLoadingDevices: PropTypes.bool.isRequired,
}

export default HardwareIndex
